import React, { useState, useEffect, useRef } from 'react';
import Table from 'react-bootstrap/Table';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

const AddUserForm = ({ user, onSave, currentUserRole }) => {
    const [formData, setFormData] = useState(user);
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false); // State to control password visibility

    // Reference for name input to set focus
    const nameInputRef = useRef(null);

    useEffect(() => {
        setFormData(user);

        // Focus on the name input when the component mounts
        if (nameInputRef.current) {
            nameInputRef.current.focus();
        }
    }, [user]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: type === 'checkbox' ? checked : (name === 'memberId' ? (value ? parseInt(value, 10) : null) : value)
        }));
        setError(''); // Clear error on input change
    };

    const handlePhoneChange = (phone) => {
        setFormData(prevState => ({
            ...prevState,
            phone
        }));
        setError('');
    };

    const validateEmail = (email) => {
        // Basic email validation using a regex pattern
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        // Validation: If role is 'Member', memberId cannot be null
        if (formData.role === 'member' && (formData.memberId === null || formData.memberId === '')) {
            setError('Member ID is required when the role is "Member".');
            return;
        }

        // Password length validation
        if (formData.password.length < 10) {
            setError('Password must be at least 10 characters.');
            return;
        }

        // Name length validation
        if (formData.name.length < 6) {
            setError('Username must be at least 6 characters.');
            return;
        }

        // Email validation
        if (!validateEmail(formData.email)) {
            setError('Please enter a valid email address.');
            return;
        }

        // Proceed to save if validation passes
        onSave(formData);
    };

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword); // Toggle password visibility
    };

    return (
        <form onSubmit={handleSubmit}>
            {error && (
                <tr>
                    <td colSpan="2" style={{ color: 'red', textAlign: 'center' }}>
                        {error}
                    </td>
                </tr>
            )}
            <Table striped bordered hover size="sm" style={{ fontSize: 12 }}>
                <tbody>
                    <tr>
                        <td>Username: <span className="text-danger">* Minimum 6 characters long, no spaces allowed</span></td>
                        <td>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                required
                                ref={nameInputRef} // Set focus to this input field
                                minLength="6" // Enforce minimum 6 characters
                                pattern="^\S+$" // Enforce no spaces allowed
                                title="Username must be at least 6 characters long and should not contain spaces"
                            />
                        </td>
                    </tr>
                    <tr>
                        <td>Password: <span className="text-danger">* Minimum 10 characters long, no spaces allowed</span></td>
                        <td>
                            <div className="d-flex align-items-center">
                                <input
                                    type={showPassword ? 'text' : 'password'}  // Toggle input type based on showPassword
                                    name="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    required
                                    minLength="10"  // Enforce minimum 10 characters
                                    pattern="^\S+$"  // Enforce no spaces allowed
                                    title="Password must be at least 10 characters long and should not contain spaces"
                                />
                                <div className="ml-2">
                                    <input
                                        type="checkbox"
                                        checked={showPassword}
                                        onChange={togglePasswordVisibility}
                                    />
                                    <label className="ml-2">Show Password</label>
                                </div>
                            </div>
                        </td>
                    </tr>
                    <tr>
                        <td>Email: <span className="text-danger">*</span></td>
                        <td>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                required
                                style={{ width: '60%' }}
                            />
                        </td>
                    </tr>
                    <tr>
                        <td>Phone:<span className="text-danger">*</span></td>
                        <td>
                            <PhoneInput
                                country={'us'} // Default country, you can change this dynamically
                                value={formData.phone}
                                onChange={handlePhoneChange}
                                inputProps={{
                                    name: 'phone',
                                    required: true,
                                }}
                                enableSearch={true}
                                disableCountryCode={false}
                                placeholder="+1 (XXX) XXX-XXXX"
                                className="phone-input-custom"
                            />
                        </td>
                    </tr>
                    <tr>
                        <td>Role: <span className="text-danger">*</span></td>
                        <td>
                            <select
                                name="role"
                                value={formData.role}
                                onChange={handleChange}
                                required
                                disabled={currentUserRole === 'ops'} // Disable if current user role is 'Ops'
                            >
                                <option value="ops">Ops</option>
                                <option value="admin">Admin</option>
                                <option value="tbd">ToBeDetermined</option>
                                <option value="viewer">Viewer</option>
                                <option value="member">Member</option>
                            </select>
                        </td>
                    </tr>

                    <tr>
                        <td>Member ID: <span className="text-danger">* Required for member role only </span></td>
                        <td>
                            <input
                                type="number"
                                name="memberId"
                                value={formData.memberId || ''}
                                onChange={handleChange}
                                disabled={formData.role !== 'member'}
                                style={{ width: '15%' }}
                            />
                        </td>
                    </tr>
                    <tr>
                        <td>Is Active:</td>
                        <td>
                            <input
                                type="checkbox"
                                name="isActive"
                                checked={formData.isActive}
                                onChange={handleChange}
                            />
                        </td>
                    </tr>
                    <tr>
                        <td>Is Deleted:</td>
                        <td>
                            <input
                                type="checkbox"
                                name="isDeleted"
                                checked={formData.isDeleted}
                                onChange={handleChange}
                            />
                        </td>
                    </tr>
                    <tr>
                        <td>Is OTP Required:</td>
                        <td>
                            <input
                                type="checkbox"
                                name="isOTPRequired"
                                checked={formData.isOTPRequired}
                                onChange={handleChange}
                            />
                        </td>
                    </tr>
                    <tr>
                        <td>Must Change Password?:<span className="text-danger">*</span></td>
                        <td>
                            <input
                                type="checkbox"
                                name="mustChangePassword"
                                checked={formData.mustChangePassword}
                                onChange={handleChange}
                                disabled={true}
                            />
                        </td>
                    </tr>
                    <tr>
                        <td colSpan="2" style={{ textAlign: 'right' }}>
                            <button type="submit" className="btn btn-primary">Save</button>
                        </td>
                    </tr>

                </tbody>
            </Table>
        </form>
    );
};

export default AddUserForm;